<script setup>
import { ref, defineProps, defineEmits, computed, onMounted, watch } from 'vue';
import { $api } from '@/services';
import Multiselect from '@suadelabs/vue3-multiselect';
import { useStore } from 'vuex';
import ConfirmSend from './ConfirmSend.vue';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';

const store = useStore();
const modalIsOpen = ref(false);
const target = ref(null);
const emit = defineEmits(['close']);
const props = defineProps({
	menuId: {
		type: String,
		required: true,
	},
	content: {
		type: String,
		required: true,
	},
});

const boardings = ref([]);
const students = ref([]);
const studentSelected = ref([]);
const targetStudent = ref(null);
const notification = ref({});
const tabs = ref([
	{
		name: 'Chọn đối tượng',
	},
	{
		name: 'Lịch sử gửi Phụ huynh',
	},
]);
const tabSelected = ref(0);
const parentNotificationHistory = ref([]);

onMounted(() => {
	fetchBoardings();
});

watch(target, () => {
	studentSelected.value = [];
});

watch(tabSelected, () => {
	if (tabSelected.value === 1) {
		fetchParentNotificationHistory();
	}
});

async function fetchBoardings() {
	try {
		const res = await $api.boarding.getDayCareClasses();
		boardings.value = res?.data?.data || [];
		students.value = res?.data?.data?.flatMap(item => item.students);
	} catch (err) {
		console.log(err);
	}
}


async function fetchParentNotificationHistory() {
	try {
		const res = await $api.menu.parentNotificationHistory(props.menuId);
		parentNotificationHistory.value = res?.data?.data || [];
	} catch (err) {
		console.log(err);
	}
}
const studentOptions = computed(() => {
	return students.value.map((item) => {
		return {
			value: item.id,
			text: item.name,
		};
	});
});
const targetOptions = computed(() => {
	const targetOptions = [
		{
			value: 'all_parents_in_org',
			text: 'PH toàn cơ sở',
		},
	];
	const dayCare = boardings.value.map((item) => {
		return {
			value: 'parent_in_assigned_class',
			text: 'PH lớp ' + item.name,
			day_care_class_id: item.id,
		};
	});
	return [
		...targetOptions,
		...dayCare,
		{
			value: 'custom_parent_of_students',
			text: 'Tuỳ chọn',
		},
	];
});
function submit() {
	let subject = null;
	if (target.value.value === 'custom_parent_of_students') {
		subject = studentSelected.value.map((item) => 'PH bé ' + item.text);
	} else subject = target.value.text;
	notification.value = {
		title: props.content,
		subject: subject,
		sender: store.state.profile.staff.name,
	};
	modalIsOpen.value = true;
}
async function sendMenuToParent() {
	try {
		modalIsOpen.value = false;
		const payload = {
			type: target.value.value,
		};

		if (target.value.value === 'custom_parent_of_students') {
			payload.student_ids = studentSelected.value.map(
				(item) => item.value
			);
		}
		if (target.value.value === 'parent_in_assigned_class') {
			payload.day_care_class_id = target.value.day_care_class_id;
		}

		await $api.menu.sendMenuToParent(props.menuId, payload);
		await store.dispatch('setAlertMessage', {
			message: 'Gửi thực đơn cho phụ huynh thành công',
			type: 'success',
		});
		emit('close');
	} catch (err) {
		modalIsOpen.value = false;
		await store.dispatch('setAlertMessage', {
			message:
				err?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
}

function addStudent(student) {
	if (studentSelected.value.some((item) => item.value === student.value)) {
		return;
	}
	studentSelected.value.push(student);
}
function deleteStudent(student) {
	studentSelected.value = studentSelected.value.filter(
		(item) => item.value !== student.value
	);
}
</script>
<template>
	<DaModalFrame v-model:open="modalIsOpen">
		<ConfirmSend
			@close="modalIsOpen = false"
			@submit="sendMenuToParent"
			:notification="notification"
		/>
	</DaModalFrame>
	<div class="fixed inset-y-0 right-0 flex side-over z-50 w-screen md:w-1/2">
		<div
			class="h-full w-full flex flex-col bg-white shadow-xl overflow-y-auto"
		>
			<div class="px-6 py-8 bg-blue-50">
				<div class="flex items-center justify-between gap-1">
					<div class="flex items-center">
						<img
							src="@/assets/images/icons/send.png"
							alt="edit"
							class="w-5 h-5"
						/>
						<div class="text-2xl font-bold text-neutral-700 ml-2">
							Gửi thực đơn cho Phụ huynh
						</div>
					</div>
					<div class="cursor-pointer" @click="emit('close')">
						<svg
							class="h-6 w-6"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							aria-hidden="true"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</div>
				</div>
			</div>
			<div class="w-full mb-10 px-4 sm:px-6 mt-4">
				<div class="border-b border-neutral-200">
					<div
						class="-mb-px flex space-x-8 tabs text-neutral-500"
						aria-label="Tabs"
					>
						<div
							v-for="(tab, index) in tabs"
							:key="index"
							:class="
								tabSelected === index
									? 'border-b-2 text-yellow-700 border-yellow-500'
									: 'text-neutral-400'
							"
							class="border-transparent hover:text-yellow-700 hover:border-yellow-500 whitespace-nowrap py-3 px-1 border-b-2 font-semibold cursor-pointer"
							@click="tabSelected = index"
						>
							{{ tab.name }}
						</div>
					</div>
				</div>
			</div>
			<div
				class="sideover-content relative flex-1 px-4 sm:px-6"
				style="scroll-behavior: smooth"
				id="slide-over"
			>
				<div v-if="tabSelected === 0">
					<div class="text-neutral-600 font-semibold mb-1">
						Đối tượng*
					</div>
					<Multiselect
						track-by="value"
						:searchable="true"
						:allow-empty="false"
						:options="targetOptions"
						v-model="target"
						label="text"
						selectLabel=""
						placeholder="Chọn đối tượng để gửi"
						selectedLabel=""
						deselectLabel="Ấn đễ bỏ chọn"
					>
						<template #noResult> Không tìm thấy kết quả </template>
					</Multiselect>
					<div
						class="w-full mt-6"
						v-if="
							target &&
							['custom_parent_of_students'].includes(target.value)
						"
					>
						<Multiselect
							track-by="value"
							:searchable="true"
							:options="studentOptions"
							v-model="targetStudent"
							label="text"
							selectLabel=""
							placeholder="Chọn phụ huynh của học sinh"
							selectedLabel=""
							deselectLabel="Ấn đễ bỏ chọn"
							@select="addStudent"
						>
							<template #noResult>
								Không tìm thấy kết quả
							</template>
						</Multiselect>
					</div>
					<div v-if="studentSelected.length > 0" class="mt-6">
						<div
							v-for="student in studentSelected"
							:key="student.id"
						>
							<div class="flex items-center justify-between mb-2">
								<div>
									Phụ huynh
									<span
										class="font-semibold text-orange-500"
										>{{ student.text }}</span
									>
								</div>
								<div
									class="flex items-center gap-1 cursor-pointer"
								>
									<img
										src="@/assets/images/icons/trash-fill-blue.png"
										alt="close"
										class="w-4 h-4"
									/>
									<div
										class="text-blue-600 font-semibold text-sm"
										@click="deleteStudent(student)"
									>
										Xoá
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div v-if="tabSelected === 1">
					<div class="overflow-hidden sm:rounded-t-2xl w-full">
						<table class="divide-y text-neutral-200 w-full">
							<thead class="bg-neutral-100 h-16">
								<tr>
									<th
										class="px-3 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										scope="col"
									>
										Ngày gửi
									</th>
									<th
										class="px-3 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										scope="col"
									>
										Đối tượng
									</th>
									<th
										class="px-3 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										scope="col"
									>
										PH đã xem
									</th>
									<th
										class="px-3 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										scope="col"
									>
										Người gửi
									</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="item in parentNotificationHistory"
									:key="item.id"
									class="bg-white text-neutral-700 border-t"
								>
									<td class="p-4 text-neutral-700" style="max-width: 125px">
										{{ item.sent_at }}
									</td>
									<td
										class="p-4 whitespace-nowrap text-neutral-700"
									>
										<div
											v-if="
												item.type ===
												'all_parents_in_org'
											"
										>
											Phụ huynh toàn cơ sở
										</div>
										<div
											v-else-if="
												item.type ===
												'custom_parent_of_students'
											"
										>
											<div
												class="flex items-center"
												v-for="student in item.student_menu_notifications"
												:key="student.id"
											>
												<div>
													<img
														class="shadow-sm inline-block h-6 w-6 rounded-full"
														:src="
															student
																?.profile_photo
																?.w200
														"
														alt=""
														v-if="
															student
																?.profile_photo
																?.w200
														"
													/>
													<span
														v-else
														class="inline-flex items-center justify-center h-6 w-6 rounded-full"
														:style="{
															background:
																student
																	?.profile_photo
																	?.default
																	?.color,
														}"
													>
														<span
															class="font-medium leading-none text-white text-xs"
															>{{
																student
																	?.profile_photo
																	?.default
																	?.initial
															}}</span
														>
													</span>
												</div>
												<div
													class="ml-2 text-yellow-700 font-semibold whitespace-nowrap"
												>
													{{ student.full_name }}
												</div>
											</div>
										</div>
										<div v-else class="flex items-center gap-2">
											<img src="@/assets/images/icons/spoon-2.png" alt="class" class="w-6 h-6">
											<div class="whitespace-nowrap">
												Lớp {{ item.day_care_class.name }}
											</div>
										</div>
									</td>
									<td
										class="p-4 whitespace-nowrap text-neutral-700"
									>
										{{ item.total_parent_viewed }} /
										{{ item.total_parent_views }}
									</td>
									<td
										class="p-4 whitespace-nowrap text-neutral-700"
									>
										<div class="flex items-center">
											<div>
												<img
													class="shadow-sm inline-block h-6 w-6 rounded-full"
													:src="
														item?.created_by_staff
															?.profile_photo
															?.w200
													"
													alt=""
													v-if="
														item?.created_by_staff
															?.profile_photo
															?.w200
													"
												/>
												<span
													v-else
													class="inline-flex items-center justify-center h-6 w-6 rounded-full"
													:style="{
														background:
															item
																?.created_by_staff
																?.profile_photo
																?.default
																?.color,
													}"
												>
													<span
														class="font-medium leading-none text-white text-xs"
														>{{
															item
																?.created_by_staff
																?.profile_photo
																?.default
																?.initial
														}}</span
													>
												</span>
											</div>
											<div
												class="ml-2 text-yellow-700 font-semibold whitespace-nowrap"
											>
												{{
													item?.created_by_staff
														.full_name
												}}
											</div>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
			<div class="px-6 py-4 border-t border-yellow-50" v-if="tabSelected === 0">
				<div class="flex justify-end">
					<div>
						<button
							type="button"
							class="button-p text-center w-32"
							@click="submit"
						>
							Xác nhận
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>